import { h } from 'preact'
import { useState, useMemo } from 'preact/hooks'
import { useStateValue } from '../../../state-management/store'
import Row from '../../../components/grid/row'
import Col from '../../../components/grid/col'
import { CustomField, CustomFieldType } from '../../custom-field'
import { ValueType, AdditionalDetails, Field } from '../../../models/patient'
import { validator } from '../../../shared/validator'
import CustomPhoneInput from '../../custom-field/custom-phone-input'
import * as style from './style.css'

const AdditionalForm: preact.FunctionalComponent = () => {
    const [
        { additionalDetails, customization, insuranceProviders, insuranceSettings, patientDetails },
        dispatch,
    ] = useStateValue()
    const [focusedField, setFocusedField] = useState<string | undefined>(undefined)

    const trim = (value?: string) => (value ? value.trim() : undefined)

    const sortedProviders = useMemo(() => {
        return insuranceProviders.filter((provider) => provider.enabled).sort((a, b) => a.name.localeCompare(b.name))
    }, [insuranceProviders, insuranceSettings])

    const showInsuranceDropdown = useMemo(() => {
        return insuranceSettings.show && insuranceProviders.some((provider) => provider.enabled)
    }, [insuranceProviders, insuranceSettings.show])

    const hasInsuranceForm =
        customization?.scheduling_additional_features_and_fields.insurance !== 'none' &&
        patientDetails.hasInsurance.value === 'yes'

    const handleFocusField = (field: string) => () => setFocusedField(field)

    const handleChange = async (fieldName: keyof AdditionalDetails, value?: ValueType) => {
        const error = validator(additionalDetails[fieldName] as Field, value)
        await dispatch({
            type: 'UPDATE_ADDITIONAL_DETAILS',
            fieldName,
            value,
            error,
        })
    }

    const showInsuranceTextField =
        !showInsuranceDropdown ||
        (insuranceSettings.other_enabled && additionalDetails.insuranceProviderSelector?.value === 'Other')

    const renderInsuranceProviderField = (label = 'Insurance Provider') => {
        if (additionalDetails.insuranceProvider) {
            return (
                <Col md={6} sm={6} xs={12}>
                    <CustomField
                        customFieldType={CustomFieldType.INPUT}
                        label={`${label}${additionalDetails.insuranceProvider.isRequired ? '*' : ''}`}
                        isDirty={additionalDetails.insuranceProvider.isDirty}
                        value={additionalDetails.insuranceProvider.value}
                        required={additionalDetails.insuranceProvider.isRequired}
                        error={additionalDetails.insuranceProvider.error}
                        onChange={(value?: ValueType) => handleChange('insuranceProvider', value)}
                        onBlur={(value?: ValueType) => handleChange('insuranceProvider', trim(value as string))}
                    />
                </Col>
            )
        }
    }

    return (
        <div class={style.additionalForm}>
            <Row class={style.additionalFormRow}>
                <Col md={5} sm={5} xs={12}>
                    <h2>Additional Information</h2>

                    {hasInsuranceForm && additionalDetails?.isPolicyHolder && (
                        <CustomField
                            label={`Patient is policy holder?${additionalDetails.isPolicyHolder.isRequired ? '*' : ''}`}
                            customFieldType={CustomFieldType.RADIOBUTTON_GROUP}
                            customLabelClass={style.radioLabelClass}
                            wrapperClassName={style.policyHolderField}
                            value={additionalDetails.isPolicyHolder.value}
                            required={additionalDetails.isPolicyHolder.isRequired}
                            error={additionalDetails.isPolicyHolder.error}
                            options={[
                                {
                                    key: 'yes',
                                    value: 'Yes',
                                },
                                {
                                    key: 'no',
                                    value: 'No',
                                },
                            ]}
                            onFocus={handleFocusField('sex')}
                            onChange={(value?: ValueType) => handleChange('isPolicyHolder', value)}
                        />
                    )}
                </Col>

                {hasInsuranceForm &&
                    additionalDetails.isPolicyHolder?.value &&
                    additionalDetails.isPolicyHolder.value === 'no' && (
                        <Col md={7} sm={7} xs={12}>
                            <Row class={style.additionalFormRow}>
                                {additionalDetails.policyHolderFirstName && (
                                    <Col md={6} sm={6} xs={6}>
                                        <CustomField
                                            customFieldType={CustomFieldType.INPUT}
                                            label={`Policy Holder First Name${
                                                additionalDetails.policyHolderFirstName.isRequired ? '*' : ''
                                            }`}
                                            wrapperClassName={style.informationFieldCollapsed}
                                            isDirty={additionalDetails.policyHolderFirstName.isDirty}
                                            value={additionalDetails.policyHolderFirstName.value}
                                            required={additionalDetails.policyHolderFirstName.isRequired}
                                            error={additionalDetails.policyHolderFirstName.error}
                                            onChange={(value?: ValueType) =>
                                                handleChange('policyHolderFirstName', value)
                                            }
                                            onBlur={(value?: ValueType) =>
                                                handleChange('policyHolderFirstName', trim(value as string))
                                            }
                                        />
                                    </Col>
                                )}
                                {additionalDetails.policyHolderLastName && (
                                    <Col md={6} sm={6} xs={6}>
                                        <CustomField
                                            customFieldType={CustomFieldType.INPUT}
                                            label={`Policy Holder Last Name${
                                                additionalDetails.policyHolderLastName.isRequired ? '*' : ''
                                            }`}
                                            wrapperClassName={style.informationFieldCollapsed}
                                            isDirty={additionalDetails.policyHolderLastName.isDirty}
                                            value={additionalDetails.policyHolderLastName.value}
                                            required={additionalDetails.policyHolderLastName.isRequired}
                                            error={additionalDetails.policyHolderLastName.error}
                                            onChange={(value?: ValueType) =>
                                                handleChange('policyHolderLastName', value)
                                            }
                                            onBlur={(value?: ValueType) =>
                                                handleChange('policyHolderLastName', trim(value as string))
                                            }
                                        />
                                    </Col>
                                )}
                            </Row>
                        </Col>
                    )}
            </Row>
            {hasInsuranceForm && (
                <Row class={style.additionalFormSecondRow}>
                    {additionalDetails?.insuranceProviderSelector && showInsuranceDropdown && (
                        <Col md={6} sm={6} xs={12}>
                            <CustomField
                                customFieldType={CustomFieldType.SEARCH_SELECT}
                                label={`Insurance Provider${
                                    additionalDetails.insuranceProvider?.isRequired ? '*' : ''
                                }`}
                                isDirty={additionalDetails.insuranceProviderSelector.isDirty}
                                value={additionalDetails.insuranceProviderSelector.value}
                                error={additionalDetails.insuranceProviderSelector.error}
                                onChange={(value?: ValueType) => {
                                    handleChange('insuranceProviderSelector', value)
                                    handleChange('insuranceProvider', value === 'Other' ? '' : value)
                                }}
                                placeholder=" "
                                items={[
                                    ...(insuranceSettings.other_enabled
                                        ? [
                                              {
                                                  name: 'Other',
                                              },
                                          ]
                                        : []),
                                    ...sortedProviders,
                                ]}
                            />
                        </Col>
                    )}

                    {additionalDetails?.insuranceProvider &&
                        showInsuranceTextField &&
                        !showInsuranceDropdown &&
                        renderInsuranceProviderField('Insurance Provider')}

                    {additionalDetails.insurancePhone && (
                        <Col sm={3} xs={6}>
                            <CustomPhoneInput
                                label="Insurance Phone Number"
                                value={additionalDetails.insurancePhone.value}
                                error={additionalDetails.insurancePhone.error}
                                rules={{ required: true }}
                                onChange={(value?: ValueType) => handleChange('insurancePhone', value as string)}
                                country={'US'}
                            />
                        </Col>
                    )}
                    {additionalDetails.insurancePhone && <Col sm={2} xs={1}></Col>}

                    {additionalDetails?.insuranceProvider &&
                        showInsuranceTextField &&
                        showInsuranceDropdown &&
                        renderInsuranceProviderField('Insurance Provider Name')}
                    {additionalDetails?.insuranceGroup && (
                        <Col md={3} sm={3} xs={6}>
                            <CustomField
                                customFieldType={CustomFieldType.INPUT}
                                label={`Insurance Group #${additionalDetails.insuranceGroup.isRequired ? '*' : ''}`}
                                tooltip="You can find your group number on the front of your insurance card."
                                isDirty={additionalDetails.insuranceGroup.isDirty}
                                value={additionalDetails.insuranceGroup.value}
                                required={additionalDetails.insuranceGroup.isRequired}
                                error={additionalDetails.insuranceGroup.error}
                                onChange={(value?: ValueType) => handleChange('insuranceGroup', value)}
                                onBlur={(value?: ValueType) => handleChange('insuranceGroup', trim(value as string))}
                            />
                        </Col>
                    )}
                    {additionalDetails.policyHolderIDSSN && (
                        <Col md={3} sm={3} xs={6}>
                            <CustomField
                                customFieldType={CustomFieldType.INPUT}
                                inputProps={{
                                    autocomplete: 'off',
                                }}
                                className={style.secureField}
                                label={`Policy Holder ID/SSN${
                                    additionalDetails.policyHolderIDSSN.isRequired ? '*' : ''
                                }`}
                                isDirty={additionalDetails.policyHolderIDSSN.isDirty}
                                value={additionalDetails.policyHolderIDSSN.value}
                                required={additionalDetails.policyHolderIDSSN.isRequired}
                                error={additionalDetails.policyHolderIDSSN.error}
                                onChange={(value?: ValueType) =>
                                    handleChange('policyHolderIDSSN', trim(value as string))
                                }
                            />
                        </Col>
                    )}
                    {additionalDetails.policyHolderDOB && additionalDetails.isPolicyHolder?.value === 'no' && (
                        <Col md={3} sm={3} xs={6}>
                            <CustomField
                                label={`Policy Holder DOB${additionalDetails.policyHolderDOB.isRequired ? '*' : ''}`}
                                customFieldType={CustomFieldType.DATEPICKER}
                                value={additionalDetails.policyHolderDOB.value}
                                required={additionalDetails.policyHolderDOB.isRequired}
                                error={additionalDetails.policyHolderDOB.error}
                                placeholder="MM/DD/YYYY"
                                onFocus={handleFocusField('policyHolderDOB')}
                                isFocused={focusedField === 'policyHolderDOB'}
                                hidePicker={true}
                                onChange={(value?: ValueType) => handleChange('policyHolderDOB', value as string)}
                            />
                        </Col>
                    )}
                </Row>
            )}

            {additionalDetails?.additionalNotes && (
                <Row class={style.additionalInformationFormRow}>
                    <Col sm={12} xs={12}>
                        <CustomField
                            customFieldType={CustomFieldType.INPUT}
                            label={`Please provide any additional information${
                                additionalDetails.additionalNotes.isRequired ? '*' : ''
                            }`}
                            isDirty={true}
                            value={additionalDetails.additionalNotes.value}
                            required={additionalDetails.additionalNotes.isRequired}
                            error={additionalDetails.additionalNotes.error}
                            onChange={(value?: ValueType) => handleChange('additionalNotes', value)}
                            onBlur={(value?: ValueType) => handleChange('additionalNotes', trim(value as string))}
                        />
                    </Col>
                </Row>
            )}
        </div>
    )
}

export default AdditionalForm
